import {
  Component,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  CustomerDAO,
  CustomerPaymentMethod,
} from '../../../../../core/services/dao/customerDAO';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { PaymentDAO } from '../../../../../core/services/dao/paymentDAO';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CharterDAO } from '../../../../../core/services/dao/charterDAO';
import { MatSelectChange } from '@angular/material/select';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import { map, startWith, take } from 'rxjs/operators';
import { UserDAO } from '../../../../../core/services/dao/userDAO';
import { User } from 'anchor-shared/lib/model/entity/user';
import { ContainsSubscriptions } from '../../../../../core/services/interfaces/contains-subscriptions';
import { unsubscribeFromAll } from '../../../../../core/pure-functions/rx-helper';
import { CharterPaymentDAO } from '../../../../../core/services/dao/charterPaymentDAO';
import { PaymentIntent, PaymentMethod } from '@stripe/stripe-js';
import { MatStepper } from '@angular/material/stepper';
import { StripePaymentDAO } from '../../../../../core/services/dao/stripePaymentDAO';
import { Payment } from 'anchor-shared/lib/model/entity/payment';
import { PayoutInfo } from 'anchor-shared/lib/model/interface/payoutInfo';
import {
  AdditionalCosts,
  IndividualCost,
} from '../../../../../core/models/interface/additional-cost';
import moment from 'moment';
import * as uuid from 'uuid';
import {
  getCharterDiscountServiceTotal,
  TransactionData,
} from '../manage-charter.component';
import { ChickenTestDialogService } from '../../../../shared/dialogs/chicken-test-dialog/chicken-test-dialog.service';
import { CharterNoteDAO } from '../../../../../core/services/dao/charterNoteDAO';
import { RatePayee } from 'anchor-shared/lib/model/entity/vessel-rate';
import { PaymentType } from 'anchor-shared/lib/model/enum/payment-type';

@Component({
  selector: 'app-add-new-transaction-dialog',
  templateUrl: './add-new-transaction-dialog.component.html',
  styleUrls: ['./add-new-transaction-dialog.component.scss'],
})
export class AddNewTransactionDialogComponent
  implements OnInit, ContainsSubscriptions, OnDestroy
{
  @ViewChild('stepper', { static: false }) stepper: MatStepper;

  paymentTitle: string;
  paymentMethod: string;
  paymentDescription: string;
  paymentAmount: string;
  subs: Subscription[] = [];
  customerPaymentMethod: CustomerPaymentMethod;
  remainderStatus: string;
  transactionForm: FormGroup;
  depositPayment: Payment;
  fullPayment: Payment;
  usePaymentMethodFromPreviousPayment: boolean;
  transId: string = null;
  charter: Charter;
  services = [];
  payoutPayees: PayoutPayee[] = [];
  filteredUsers: Observable<User[]>;
  additionalCosts: AdditionalCosts = {
    fuelCosts: {
      gallons: null,
      cost: '0.00',
      costPerGal: null,
    },
    additionalCharges: {
      cost: 0.0,
      individualCosts: [],
    },
    total: '0.00',
  };

  readOnlyTransactionData;
  transactionType: string;
  autoControl: FormControl = new FormControl();
  userOptions: User[];
  specificUser: User;
  paymentIntent: PaymentIntent;
  paymentMethods: PaymentMethod[];
  paymentReady: boolean;
  splitPayout: boolean = false;
  payoutLimit: number = 5;
  isMobile: boolean;
  RatePayee = RatePayee;

  constructor(
    private dialogRef: MatDialogRef<AddNewTransactionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private paymentDAO: PaymentDAO,
    private customerDAO: CustomerDAO,
    private charterDAO: CharterDAO,
    private charterNoteDAO: CharterNoteDAO,
    private ngZone: NgZone,
    private userDao: UserDAO,
    private charterPaymentDao: CharterPaymentDAO,
    private stripePaymentDao: StripePaymentDAO,
    private snackBar: MatSnackBar,
    private chickenTestDialog: ChickenTestDialogService,
  ) {
    this.remainderStatus = data.remainderStatus;
    this.depositPayment = data.depositPayment;
    this.fullPayment = data.fullPayment;
    this.charter = data.charter;
    this.services = [...this.charter.services];
    this.isMobile = data.isMobile;
    this.readOnlyTransactionData = data.readOnlyTransactionData;
    this.transactionType = data.transactionType;

    this.usePaymentMethodFromPreviousPayment = false;

    this.transactionForm = new FormGroup({
      paymentType: new FormControl(
        this.readOnlyTransactionData?.paymentType || '',
        [Validators.required],
      ),
      paymentAmount: new FormControl(
        this.readOnlyTransactionData?.paymentAmount || '',
        [Validators.required],
      ),
      paymentTitle: new FormControl(
        this.readOnlyTransactionData?.paymentTitle || '',
        [Validators.required],
      ),
      paymentDescription: new FormControl(
        this.readOnlyTransactionData?.paymentDescription || '',
        [Validators.required],
      ),
    });

    if (this.readOnlyTransactionData) {
      this.initReadOnlyTransactions();

      if (this.readOnlyTransactionData.paymentAmount) {
        this.transactionForm.controls.paymentAmount.disable();
      }
      if (this.readOnlyTransactionData.paymentTitle) {
        this.transactionForm.controls.paymentTitle.disable();
      }
      if (this.readOnlyTransactionData.paymentDescription) {
        this.transactionForm.controls.paymentDescription.disable();
      }
    } else {
      this.addPayee();
    }
  }

  ngOnInit() {
    this.initPreviousPayments();

    const userSub = this.userDao.getUsersForPayouts().subscribe((userData) => {
      this.userOptions = userData;
      this.filteredUsers = this.autoControl.valueChanges.pipe(
        startWith(''),
        map((val) => (val && val.length >= 4 ? this._filter(val) : [])),
      );
    });
    this.subs.push(userSub);
  }

  _filter(val: string): User[] {
    return this.userOptions.filter((s) =>
      new RegExp(val, 'gi').test(s.firstName + ' ' + s.lastName),
    );
  }

  selectUser($event, user: User, index: number) {
    if ($event.isUserInput) {
      this.payoutPayees[index].payoutPayeeUserId = user.id;
      this.specificUser = user;
    }
  }

  initReadOnlyTransactions() {
    this.paymentMethod = this.readOnlyTransactionData.paymentType || '';
    this.paymentAmount = this.readOnlyTransactionData.paymentAmount;
    this.paymentTitle = this.readOnlyTransactionData.paymentTitle;
    this.paymentDescription = this.readOnlyTransactionData.paymentDescription;
    if (this.readOnlyTransactionData.payoutPayees?.length > 0) {
      this.readOnlyTransactionData.payoutPayees.forEach((payoutPayee) => {
        this.addPayee({
          payoutPayeeType: payoutPayee.payoutPayeeType,
          payoutAmount: payoutPayee.payoutAmount,
        });
      });
    }
  }

  initPreviousPayments() {
    const depositAndFullPayments = [this.depositPayment, this.fullPayment];

    depositAndFullPayments.some((payment) => {
      if (payment && payment.transactionId) {
        this.transId = payment.transactionId;
        return;
      }
    });
  }

  async closeDialog(adminResponse: boolean, paymentIntent?: PaymentIntent) {
    let response: Partial<OneOffTransactionResponse>;
    if (adminResponse) {
      const payoutPayees = this.payoutPayees.map((payee) => {
        return {
          payoutPayeeType: payee.payoutPayeeType,
          payoutAmount: Number(payee.payoutAmount),
          payoutPayeeUserId: payee.payoutPayeeUserId,
          payoutId: payee.payoutId,
        };
      });

      let payoutInfo;
      if (payoutPayees.length > 1) {
        payoutInfo = {
          ...payoutPayees[0],
          subPayoutInfo: payoutPayees,
        };
      } else {
        payoutInfo = payoutPayees[0];
      }

      let individualCost: IndividualCost = {
        payee: 'Anchor',
        total:
          this.transactionForm.value.paymentAmount ||
          this.readOnlyTransactionData.paymentAmount,
        fullName: 'Anchor',
        payeePay:
          this.transactionForm.value.paymentAmount ||
          this.readOnlyTransactionData.paymentAmount,
        description:
          this.transactionForm.value.paymentDescription ||
          this.readOnlyTransactionData.paymentDescription,
        title:
          this.transactionForm.value.paymentTitle ||
          this.readOnlyTransactionData.paymentTitle,
        type: 'manual',
        charterPaidAtCreation: this.charter.remainderStatus === 'PAID',
        createdAt: moment().toDate(),
        uuid: uuid.v4(),
        // paymentId: result.id,
        payoutInfo,
      };

      const manualTransactionData: TransactionData = {
        amount: parseFloat(
          this.transactionForm.value.paymentAmount ||
            this.readOnlyTransactionData.paymentAmount,
        ).toFixed(2),
        // userId: result.customerId,
        description:
          this.transactionForm.value.paymentDescription ||
          this.readOnlyTransactionData.paymentDescription,
        name:
          this.transactionForm.value.paymentTitle ||
          this.readOnlyTransactionData.paymentTitle,
        charterId: this.charter.id,
        remainderStatus: this.charter.remainderStatus,
        transId: this.transId,
        clonePreviousPayment: this.usePaymentMethodFromPreviousPayment,
        payoutInfo,
        transactions: { ...paymentIntent },
      };

      response = {
        adminResponse: adminResponse,
        paymentTitle:
          this.transactionForm.value.paymentTitle ||
          this.readOnlyTransactionData.paymentTitle,
        paymentDescription:
          this.transactionForm.value.paymentDescription ||
          this.readOnlyTransactionData.paymentDescription,
        paymentMethod: this.paymentMethod,
        paymentAmount:
          this.transactionForm.value.paymentAmount ||
          this.readOnlyTransactionData.paymentAmount,
        clonePreviousPayment: this.usePaymentMethodFromPreviousPayment,
        transId: this.transId,
        paymentIntent: paymentIntent,
        individualCost,
        manualTransactionData,
      };

      if (this.paymentMethod === 'braintree') {
        response.customerPaymentMethod = this.customerPaymentMethod;
      }

      let stringForCharterNotes;
      let payment;
      if (this.paymentMethod === 'other') {
        individualCost.type = 'other';
        stringForCharterNotes = 'added as "other" transaction for:';
        if (individualCost.charterPaidAtCreation) {
          individualCost.type = 'other';
        }
        payment = await this.createOtherPayment(
          manualTransactionData,
          individualCost,
        )
          .first()
          .toPromise();
        this.openSnackbar(
          'Other payment added. \n The charter agreement has changed, please re-send it to the customer to be signed again.',
        );
      } else if (this.paymentMethod === 'stripe') {
        stringForCharterNotes = 'sent to Stripe for';
        payment = await this.createPaymentRecordForPaidCharter(
          manualTransactionData,
          individualCost,
          response,
        )
          .first()
          .toPromise();
        response.payment = payment;
        if (this.readOnlyTransactionData?.transactionType !== 'adjustment') {
          this.addTransactionToAdditionalCostsAndServices(individualCost, null);
        }
        await this.updateCharterWithNewTransactionInfo().first().toPromise();
        this.openSnackbar(
          'Payment successfully created. The charter agreement has changed, ' +
            'please regenerate it and re-send it to the customer to be signed again.',
        );
      } else if (
        (this.charter.paymentReceived !== true &&
          this.paymentMethod === 'bundle') ||
        this.paymentMethod === 'stripe'
      ) {
        stringForCharterNotes = 'added to full payment for';
        let additionalCost;
        if (this.readOnlyTransactionData?.transactionType === 'adjustment') {
          additionalCost = this.readOnlyTransactionData.paymentAmount;
        } else {
          this.charter.serviceTotal += parseFloat(
            this.transactionForm.value.paymentAmount ||
              this.readOnlyTransactionData.paymentAmount,
          );
          additionalCost = this.transactionForm.value.paymentAmount;
          this.addTransactionToAdditionalCostsAndServices(individualCost, null);
          await this.updateCharterWithNewTransactionInfo().first().toPromise();
        }

        const paymSub = this.paymentDAO
          .getPaymentsByCharterId(this.charter.id)
          .subscribe((payments) => {
            if (payments.length > 0) {
              const fullPayment = payments.find((payment) => {
                return payment.type === PaymentType.FULL;
              });
              if (fullPayment) {
                const newAmount = (
                  Number(fullPayment.amount) + Number(additionalCost)
                ).toFixed(2);
                const paymUpdateSub = this.paymentDAO
                  .updateFromPartial(fullPayment.id, {
                    amount: newAmount,
                  })
                  .subscribe();
                this.subs.push(paymUpdateSub);
              }
            }
          });
        this.subs.push(paymSub);
      } else if (
        this.charter.remainderStatus === 'PAID' &&
        this.paymentMethod === 'braintree' &&
        !this.customerPaymentMethod
      ) {
        this.openSnackbar(
          'No user account found for this charter. Please reach out to the customer ' +
            'and have them visit www.anchorrides.com to sign up. ',
        );
      }

      response.payment = payment;

      const standardPayments = this.charter.payments.filter(
        (payment) =>
          !payment.manual ||
          (payment.paymentIntentId &&
            payment.paymentIntentId.includes('pi') &&
            payment.transactions),
      );

      if (standardPayments && standardPayments.length) {
        if (
          standardPayments[0].type === 'full' &&
          standardPayments[0].paid === false
        ) {
          this.chickenTestDialog.open(
            null,
            'Ok',
            null,
            null,
            null,
            'Be sure to resend the full payment request after adding the add-on transaction.',
          );
        }
      } else {
        this.openSnackbar(
          'Payment successfully bundled into full payment.' +
            ' The charter agreement has changed, please regenerate it and re-send it to the customer to be signed again.',
        );
      }

      const paymentNoteSub = this.charterNoteDAO
        .saveNote(
          this.charter.id,
          `Transaction added:\n
        $${response.paymentAmount} ${stringForCharterNotes} ${response.paymentTitle} - ${response.paymentDescription}`,
        )
        .subscribe();
      this.subs.push(paymentNoteSub);
    }
    this.dialogRef.close(response);
  }

  openSnackbar(message: string) {
    this.snackBar.open(message, null, {
      duration: 5000,
      verticalPosition: 'top',
    });
  }

  positiveNumberOnly(event): Boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode === 45) {
      return false;
    }
    return true;
  }

  changePayoutPayeeType($event: MatSelectChange, index: number) {
    if ($event.value !== 'specific user') {
      this.payoutPayees[index].showSpecificUserSelection = false;
      this.specificUser = null;
    }

    if ($event.value === RatePayee.ANCHOR) {
      this.payoutPayees[index].payoutPayeeUserId = null;
    } else if ($event.value === RatePayee.BOAT_OWNER) {
      this.payoutPayees[index].payoutPayeeUserId =
        this.charter.boat.owner.user.id;
    } else if ($event.value === RatePayee.CAPTAIN) {
      if (this.charter.captain) {
        this.payoutPayees[index].payoutPayeeUserId =
          this.charter.captain.userId;
      } else {
        this.payoutPayees[index].payoutPayeeUserId = null;
      }
    } else if ($event.value === RatePayee.CREW) {
      if (this.charter.crew.length === 1) {
        // only assing crew user if one crew member is assigned
        this.payoutPayees[index].payoutPayeeUserId =
          this.charter.crew[0].user.id;
      } else {
        this.payoutPayees[index].payoutPayeeUserId = null;
      }
    } else if ($event.value === RatePayee.SPECIFIC_USER) {
      this.payoutPayees[index].showSpecificUserSelection = true;
    }
  }

  addPayee(options?) {
    const defaultPayee = {
      payoutPayeeType: options?.payoutPayeeType || '',
      payoutAmount: options?.payoutAmount || '',
      payoutPayeeUserId: null,
      payoutId: Date.now(),
      showSpecificUserSelection: false,
      payeeForm: new FormGroup({
        payoutPayeeType: new FormControl(options?.payoutPayeeType || '', [
          Validators.required,
        ]),
        payoutAmount: new FormControl(options?.payoutAmount || '', [
          Validators.required,
        ]),
      }),
    };
    if (options?.payoutPayeeType) {
      defaultPayee.payeeForm.controls.payoutPayeeType.disable();
    }
    if (options?.payoutAmount) {
      defaultPayee.payeeForm.controls.payoutAmount.disable();
      defaultPayee.payeeForm.controls.payoutAmount.enable({ emitEvent: false });
      const isValid = defaultPayee.payeeForm.controls.payoutAmount.valid;
      defaultPayee.payeeForm.controls.payoutAmount.disable({
        emitEvent: false,
      });
      const isValid2 = defaultPayee.payeeForm.controls.payoutAmount.valid;
    }
    this.payoutPayees.push(defaultPayee);
  }

  removePayee(index) {
    this.payoutPayees.splice(index, 1);
  }

  payoutPayeesValidation() {
    return (
      this.transactionForm.valid &&
      this.payoutPayees
        .filter((input) => !input.payeeForm.disabled)
        .every((payee) => payee.payeeForm.valid)
    );
  }

  payoutGreaterThanPayment() {
    const payoutTotal = this.payoutPayees.reduce(
      (acc, curr) => Number(curr.payoutAmount) + acc,
      0,
    );
    return payoutTotal > parseFloat(this.paymentAmount);
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  createPaymentIntent(): Observable<PaymentIntent> {
    return this.charterPaymentDao.createAdminPaymentIntent(
      this.charter.id,
      this.charter.userId,
      this.paymentAmount,
      this.paymentTitle,
      this.paymentDescription,
    );
  }

  listCustomerPaymentMethods(): Observable<PaymentMethodData> {
    return this.stripePaymentDao.adminListCustomerPaymentMethods(
      this.charter.userId,
    );
  }

  continuePaymentFlow() {
    if (this.paymentMethod === 'other' || this.paymentMethod === 'bundle') {
      this.closeDialog(true);
    } else if (this.paymentMethod === 'stripe') {
      const obs: Observable<any>[] = [
        this.createPaymentIntent(),
        this.listCustomerPaymentMethods(),
      ];
      const paymentIntentSub = combineLatest(obs).subscribe(
        ([paymentIntent$, paymentMethods$]) => {
          this.paymentReady = true;
          this.paymentIntent = paymentIntent$;
          this.paymentMethods = paymentMethods$.data;
          this.stepper.next();
        },
      );

      this.subs.push(paymentIntentSub);
    }
  }

  // payment management

  createOtherPayment(manualTransactionData, individualCost: IndividualCost) {
    const paymentSubject = new Subject<Payment>();
    individualCost.type = 'other';
    manualTransactionData.paid = true;
    manualTransactionData.failed = false;
    manualTransactionData.manual = true;
    manualTransactionData.notes = manualTransactionData.description;
    manualTransactionData.name = `${manualTransactionData.name} - Other`;
    manualTransactionData.transactionCreatedAt = moment().toDate();
    if (this.readOnlyTransactionData?.transactionType === 'adjustment') {
      manualTransactionData.type = PaymentType.ADJUSTMENT;
    } else {
      manualTransactionData.type = PaymentType.MANUAL;
    }
    const otherPaymentSub = this.paymentDAO
      .createManualPayment(manualTransactionData)
      .subscribe(async (payment) => {
        this.charter.payments.push(payment);
        if (this.readOnlyTransactionData?.transactionType !== 'adjustment') {
          this.addTransactionToAdditionalCostsAndServices(
            individualCost,
            payment.id,
          );
          await this.updateCharterWithNewTransactionInfo().first().toPromise();
        }
        paymentSubject.next(payment);
      });
    this.subs.push(otherPaymentSub);

    return paymentSubject.asObservable();
  }

  createPaymentRecordForPaidCharter(
    manualTransactionData,
    individualCost: IndividualCost,
    result,
  ) {
    const paymentSubject = new Subject<Payment>();
    manualTransactionData.customerPaymentMethod = result.customerPaymentMethod;
    individualCost.charterPaidAtCreation = true;
    if (this.readOnlyTransactionData?.transactionType === 'adjustment') {
      manualTransactionData.type = PaymentType.ADJUSTMENT;
    }
    const manualPaymentSub = this.paymentDAO
      .addManualStripeTransaction(
        manualTransactionData,
        this.charter.customerEmail,
      )
      .subscribe((payment) => {
        if (
          payment &&
          this.readOnlyTransactionData?.transactionType === 'adjustment'
        ) {
          this.paymentDAO
            .deletePayment(this.readOnlyTransactionData.paymentId)
            .subscribe((success) => {
              paymentSubject.next(payment);
            });
        } else {
          paymentSubject.next(payment);
        }
      });
    this.subs.push(manualPaymentSub);

    return paymentSubject.asObservable();
  }

  addTransactionToAdditionalCostsAndServices(
    individualCost: IndividualCost,
    paymentId?: number,
  ) {
    if (paymentId) {
      individualCost.paymentId = paymentId;
    }

    const service = {
      cost: individualCost.total,
      name: individualCost.title,
      show: true,
      created: individualCost.createdAt,
      description: individualCost.description,
      uuid: individualCost.uuid,
      paymentId: paymentId ? paymentId : null,
    };

    if (!this.charter.additionalCosts) {
      this.charter.additionalCosts = this.additionalCosts;
    }
    this.charter.additionalCosts.additionalCharges.individualCosts.push(
      individualCost,
    );
    this.services.push(service);
  }

  updateCharterWithNewTransactionInfo() {
    const updateSubject = new Subject();
    const updateCharterSub = this.charterDAO
      .updateFromPartial(this.charter.id, {
        serviceTotal: this.charter.serviceTotal,
        additionalCosts: this.charter.additionalCosts,
        services: this.services,
        agreementCreatedDate: this.isCharterPaid()
          ? this.charter.agreementCreatedDate
          : null,
        agreementSignedDate: this.isCharterPaid()
          ? this.charter.agreementSignedDate
          : null,
        agreementUrl: this.isCharterPaid() ? this.charter.agreementUrl : null,
        signedAgreementUrl: this.isCharterPaid()
          ? this.charter.signedAgreementUrl
          : null,
      })
      .subscribe(() => {
        updateSubject.next();
        updateSubject.complete();
      });
    this.subs.push(updateCharterSub);

    return updateSubject.asObservable();
  }

  isCharterPaid(): boolean {
    return this.charter.remainderStatus === 'PAID';
  }
}

export interface OneOffTransactionResponse {
  adminResponse: boolean;
  paymentTitle: string;
  paymentDescription: string;
  paymentMethod: string;
  paymentAmount: number;
  customerPaymentMethod?: CustomerPaymentMethod;
  clonePreviousPayment?: boolean;
  transId?: string;
  payoutInfo: PayoutInfo;
  paymentNonce?: string;
  paymentIntent: PaymentIntent;
  individualCost: IndividualCost;
  manualTransactionData: Partial<OneOffTransactionResponse>;
  payment?: Payment;
}

export interface PayoutPayee {
  payoutPayeeType: string;
  payoutAmount: string;
  payoutPayeeUserId: number;
  payoutId: number;
  showSpecificUserSelection: boolean;
  payeeForm: FormGroup;
}
